import React from "react";
import { motion } from 'framer-motion';
import { FiAward, FiUsers, FiTrendingUp, FiTarget, FiCheck, FiArrowRight } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const AboutPage: React.FC = () => {
  const stats = [
    { number: '150+', label: 'Brands Supported', icon: FiUsers },
    { number: '4.9', label: 'Client Rating', icon: FiAward },
    { number: '12+', label: 'Countries Served', icon: FiTrendingUp },
    { number: '10M+', label: 'Leads Generated', icon: FiTarget },
  ];

  const values = [
    {
      title: 'Client-First Approach',
      description: 'Your success is our success. We prioritize your business goals and work tirelessly to achieve them.',
      icon: '🎯'
    },
    {
      title: 'Data-Driven Results',
      description: 'Every strategy is backed by data and analytics to ensure maximum ROI and measurable growth.',
      icon: '📊'
    },
    {
      title: 'Transparent Communication',
      description: 'Clear, honest communication at every step. You\'ll always know what we\'re doing and why.',
      icon: '💬'
    },
    {
      title: 'Innovation & Excellence',
      description: 'We stay ahead of digital marketing trends to provide cutting-edge solutions that work.',
      icon: '🚀'
    }
  ];

  const timeline = [
    { year: '2018', title: 'Foundation', description: 'Started with a vision to transform digital marketing in India' },
    { year: '2019', title: 'Growth Phase', description: 'Expanded services and built a talented team of 20+ specialists' },
    { year: '2021', title: 'International Reach', description: 'Served clients across 12+ countries with proven results' },
    { year: '2023', title: 'Industry Recognition', description: 'Awarded Best Digital Marketing Agency and 150+ successful campaigns' },
    { year: '2025', title: 'Innovation Leader', description: 'Leading AI-driven marketing solutions and enterprise services' }
  ];

  const team = [
    {
      name: 'Mohamad Bidiwala',
      position: 'CEO & Founder',
      image: '/images/team/mohamad-bidiwala.jpg',
      bio: 'Visionary leader with 12+ years in digital marketing',
      linkedin: '#'
    },
    {
      name: 'Zaid Shaikh ',
      position: 'Manager',
      image: '/images/team/zaid-shaikh.jpg',
      bio: 'Operations expert ensuring seamless project delivery',
      linkedin: '#'
    },
    {
      name: 'Jay Narayan Bihani',
      position: 'Social Media Manager',
      image: '/images/team/jay-narayan-bihani.jpg',
      bio: 'Award-winning designer crafting compelling brand stories',
      linkedin: '#'
    },
    {
      name: 'Tejash Yadav',
      position: 'Senior Multi-Media Executive',
      image: '/images/team/tejash-yadav.jpg',
      bio: 'Building and nurturing our talented team culture',
      linkedin: '#'
    },
    {
      name: 'Pathan Gulam Khalid',
      position: 'Multi-Media Executive',
      image: '/images/team/pathan-gulam-khalid.jpg',
      bio: 'PPC expert managing multi-million dollar campaigns',
      linkedin: '#'
    },
    {
      name: 'Kartik Gamit',
      position: 'Google Ads Executive',
      image: '/images/team/kartik-gamit.jpg',
      bio: 'Building and nurturing our talented team culture',
      linkedin: '#'
    },
    {
      name: 'Ijani Uzair',
      position: 'SEO & Ai SEO Executive',
      image: '/images/team/ijani-uzair.jpg',
      bio: 'Building and nurturing our talented team culture',
      linkedin: '#'
    },
    {
      name: 'Sanchit Jha',
      position: 'Full Stack Developer',
      image: '/images/team/sanchit-jha.jpeg',
      bio: 'Full-stack developer leading our tech innovations',
      linkedin: '#'
    },
    {
      name: 'Nofil ',
      position: 'Brand Executive',
      image: '/images/team/nofil.jpg',
      bio: 'Social media expert with viral campaign experience',
      linkedin: '#'
    }
  ];

  const openPositions = [
    {
      title: 'Senior SEO Specialist',
      department: 'SEO Team',
      location: 'Mumbai / Remote',
      type: 'Full-time',
      description: 'Lead SEO strategies for enterprise clients and mentor junior team members.'
    },
    {
      title: 'PPC Campaign Manager',
      department: 'Paid Marketing',
      location: 'Bangalore / Hybrid',
      type: 'Full-time',
      description: 'Manage and optimize Google Ads and Meta Ads campaigns for various industries.'
    },
    {
      title: 'Content Writer',
      department: 'Content Team',
      location: 'Remote',
      type: 'Full-time',
      description: 'Create compelling content for blogs, websites, and marketing campaigns.'
    },
    {
      title: 'Web Developer',
      department: 'Development',
      location: 'Delhi / Hybrid',
      type: 'Full-time',
      description: 'Build and maintain WordPress and custom websites for clients.'
    },
    {
      title: 'Graphic Designer',
      department: 'Creative Team',
      location: 'Mumbai / Hybrid',
      type: 'Full-time',
      description: 'Design stunning visuals for social media, ads, and brand materials.'
    },
    {
      title: 'Digital Marketing Intern',
      department: 'Multiple Teams',
      location: 'Remote',
      type: 'Internship',
      description: 'Learn and grow with hands-on experience in various digital marketing disciplines.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden min-h-[600px] flex items-center">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=1920&h=1080&fit=crop"
            alt="Team collaboration"
            className="w-full h-full object-cover brightness-110 contrast-110"
          />
          {/* Light Gradient Overlay for Better Text Contrast */}
          <div className="absolute inset-0 bg-gradient-to-br from-black/30 via-black/20 to-black/30"></div>
          {/* Animated Dots Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px'
            }}></div>
          </div>
          {/* Floating Particles */}
          <div className="absolute inset-0">
            {[...Array(20)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-2 h-2 bg-white rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                }}
                animate={{
                  y: [0, -30, 0],
                  opacity: [0.2, 0.6, 0.2],
                  scale: [1, 1.5, 1],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </div>
        
        <div className="container-width relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-4xl mx-auto text-center"
          >
            <motion.h1 
              className="text-5xl md:text-6xl lg:text-7xl font-extrabold mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <span className="text-white" style={{ 
                textShadow: '0 4px 30px rgba(0,0,0,0.9), 0 2px 10px rgba(0,0,0,0.8)'
              }}>
                About{' '}
              </span>
              <span className="text-orange-500" style={{
                textShadow: '0 4px 30px rgba(0,0,0,0.9), 0 2px 10px rgba(0,0,0,0.8)'
              }}>
                Us
              </span>
            </motion.h1>
            <motion.p 
              className="text-xl md:text-2xl text-white leading-relaxed font-bold"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              style={{ 
                textShadow: '0 4px 20px rgba(0,0,0,0.95), 0 2px 10px rgba(0,0,0,0.9)'
              }}
            >
              Mirani Marketing is a team of passionate digital marketers helping businesses grow through smart and creative online strategies. We believe marketing should not feel confusing or overwhelming—our goal is to make it simple, effective, and results-driven for every client we work with.
            </motion.p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            {stats.map((stat, index) => (
              <motion.div 
                key={index} 
                className="text-center backdrop-blur-md bg-white/10 rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all duration-300"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                whileHover={{ 
                  scale: 1.05, 
                  y: -5,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                }}
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <stat.icon className="w-8 h-8 mx-auto mb-3 text-orange-300" />
                </motion.div>
                <div className="text-4xl font-bold mb-2 text-white">{stat.number}</div>
                <div className="text-blue-100">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white relative overflow-hidden">
        {/* Background Decorations */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary-100 rounded-full filter blur-3xl opacity-20 -z-0"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-100 rounded-full filter blur-3xl opacity-20 -z-0"></div>
        
        <div className="container-width relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="text-4xl font-bold text-gray-800 mb-6 relative inline-block">
                  About Company
                  <motion.div 
                    className="absolute -bottom-2 left-0 h-1 bg-gradient-to-r from-primary-600 to-orange-500 rounded-full"
                    initial={{ width: 0 }}
                    whileInView={{ width: '100%' }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  ></motion.div>
                </h2>
              </motion.div>
              <motion.p 
                className="text-lg text-gray-600 mb-4 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Mirani Marketing is a team of passionate digital marketers helping businesses grow through smart and creative online strategies. We believe marketing should not feel confusing or overwhelming—our goal is to make it simple, effective, and results-driven for every client we work with.
              </motion.p>
              <motion.p 
                className="text-lg text-gray-600 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                We focus on improving your online presence, bringing the right audience to your business, and helping you achieve real, measurable growth. Whether it's through search engine optimization, paid advertising, social media, or web development—we're here to make digital marketing work for you.
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              className="relative group"
            >
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
                className="relative"
              >
                <img 
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop"
                  alt="Team collaboration"
                  className="rounded-2xl shadow-2xl relative z-10"
                />
                {/* Animated Border */}
                <motion.div 
                  className="absolute inset-0 rounded-2xl border-4 border-primary-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  animate={{ 
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                ></motion.div>
                {/* Glow Effect */}
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-400 to-orange-400 rounded-2xl blur-2xl opacity-20 group-hover:opacity-40 transition-opacity duration-300 -z-10"></div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden">
        {/* Animated Background Elements */}
        <motion.div 
          className="absolute top-20 left-10 w-20 h-20 border-4 border-primary-300 rounded-full"
          animate={{ 
            rotate: 360,
            scale: [1, 1.2, 1],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "linear" 
          }}
        ></motion.div>
        <motion.div 
          className="absolute bottom-20 right-10 w-32 h-32 border-4 border-orange-300 rounded-full"
          animate={{ 
            rotate: -360,
            scale: [1, 1.3, 1],
          }}
          transition={{ 
            duration: 15, 
            repeat: Infinity,
            ease: "linear" 
          }}
        ></motion.div>
        
        <div className="container-width relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="inline-block mb-4"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-primary-100 to-orange-100 text-primary-700 rounded-full text-sm font-semibold">
                What Drives Us
              </span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Our Core Values</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 relative overflow-hidden group"
              >
                {/* Animated Background Gradient */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-br from-primary-50 to-orange-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  initial={{ scale: 0 }}
                  whileHover={{ scale: 1 }}
                ></motion.div>
                
                <div className="relative z-10">
                  <motion.div 
                    className="text-5xl mb-4 inline-block"
                    whileHover={{ 
                      rotate: [0, -10, 10, -10, 0],
                      scale: [1, 1.2, 1.2, 1.2, 1]
                    }}
                    transition={{ duration: 0.5 }}
                  >
                    {value.icon}
                  </motion.div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3 group-hover:text-primary-600 transition-colors">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </div>

                {/* Corner Accent */}
                <motion.div 
                  className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-primary-200 to-orange-200 rounded-full opacity-0 group-hover:opacity-20 blur-2xl transition-opacity duration-300"
                ></motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-12 bg-white">
        <div className="container-width">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Our Journey</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Milestones that shaped our growth
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {timeline.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="flex gap-8 mb-12 relative"
              >
                {/* Timeline line */}
                {index !== timeline.length - 1 && (
                  <div className="absolute left-[47px] top-16 w-0.5 h-full bg-primary-200"></div>
                )}
                
                <div className="flex-shrink-0 w-24">
                  <div className="bg-primary-600 text-white rounded-full w-16 h-16 flex items-center justify-center font-bold text-sm relative z-10">
                    {item.year}
                  </div>
                </div>
                <div className="flex-1 bg-gray-50 rounded-xl p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-12 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
        {/* Floating Shapes */}
        <motion.div
          className="absolute top-40 left-20 w-64 h-64 bg-primary-200 rounded-full filter blur-3xl opacity-20"
          animate={{ 
            x: [0, 50, 0],
            y: [0, 30, 0],
          }}
          transition={{ 
            duration: 8, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        ></motion.div>
        <motion.div
          className="absolute bottom-40 right-20 w-64 h-64 bg-orange-200 rounded-full filter blur-3xl opacity-20"
          animate={{ 
            x: [0, -50, 0],
            y: [0, -30, 0],
          }}
          transition={{ 
            duration: 10, 
            repeat: Infinity,
            ease: "easeInOut" 
          }}
        ></motion.div>
        
        <div className="container-width relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, type: "spring" }}
              className="inline-block mb-4"
            >
              <span className="px-6 py-2 bg-gradient-to-r from-primary-100 to-orange-100 text-primary-700 rounded-full text-sm font-semibold">
                Our People
              </span>
            </motion.div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Meet Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The talented people behind our success
            </p>
          </motion.div>

          {/* CEO & Founder Section */}
          <div className="flex justify-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              whileHover={{ y: -10 }}
              className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group relative w-full max-w-sm"
            >
              <div className="relative overflow-hidden h-80">
                <motion.img 
                  src={team[0].image}
                  alt={team[0].name}
                  className="w-full h-full object-contain bg-gray-50"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.4 }}
                />
                {/* Gradient Overlay on Hover */}
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-t from-primary-600/90 via-primary-600/50 to-transparent"
                  initial={{ opacity: 0 }}
                  whileHover={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                ></motion.div>
                
                {/* Social Icon on Hover */}
                <motion.a
                  href={team[0].linkedin}
                  className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                  whileHover={{ scale: 1.2, rotate: 360 }}
                  transition={{ duration: 0.3 }}
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                  </svg>
                </motion.a>
              </div>
              <div className="p-6 relative">
                {/* Decorative Corner */}
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                
                <h3 className="text-xl font-bold text-gray-800 mb-1 relative z-10">{team[0].name}</h3>
                <p className="text-primary-600 font-semibold mb-3">{team[0].position}</p>
                <p className="text-gray-600 text-sm">{team[0].bio}</p>
              </div>
            </motion.div>
          </div>

          {/* Team Members Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {team.slice(1).map((member, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group relative"
              >
                <div className="relative overflow-hidden h-64">
                  <motion.img 
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-contain bg-gray-50"
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.4 }}
                  />
                  {/* Gradient Overlay on Hover */}
                  <motion.div 
                    className="absolute inset-0 bg-gradient-to-t from-primary-600/90 via-primary-600/50 to-transparent"
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.3 }}
                  ></motion.div>
                  
                  {/* Social Icon on Hover */}
                  <motion.a
                    href={member.linkedin}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 bg-white rounded-full flex items-center justify-center text-primary-600 opacity-0 group-hover:opacity-100 transition-opacity"
                    whileHover={{ scale: 1.2, rotate: 360 }}
                    transition={{ duration: 0.3 }}
                  >
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                    </svg>
                  </motion.a>
                </div>
                <div className="p-6 relative">
                  {/* Decorative Corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-primary-100 to-transparent rounded-bl-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
                  
                  <h3 className="text-xl font-bold text-gray-800 mb-1 relative z-10">{member.name}</h3>
                  <p className="text-primary-600 font-semibold mb-3">{member.position}</p>
                  <p className="text-gray-600 text-sm">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers Section */}
      <section className="py-20 bg-white">
        <div className="container-width">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-800 mb-4">Join Our Team</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We're always looking for talented individuals to join our growing team
            </p>
          </motion.div>

          {/* Why Work With Us */}
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 md:p-12 mb-12">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Why Work With Us?</h3>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: '🚀', title: 'Growth Opportunities', desc: 'Clear career paths and continuous learning' },
                { icon: '💰', title: 'Competitive Salary', desc: 'Industry-leading compensation and benefits' },
                { icon: '🏖️', title: 'Work-Life Balance', desc: 'Flexible hours and remote work options' },
                { icon: '🎯', title: 'Challenging Projects', desc: 'Work with top brands and exciting campaigns' },
                { icon: '👥', title: 'Amazing Culture', desc: 'Collaborative and supportive team environment' },
                { icon: '🎓', title: 'Learning Budget', desc: 'Annual budget for courses and conferences' }
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <div className="text-3xl">{benefit.icon}</div>
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">{benefit.title}</h4>
                    <p className="text-gray-600 text-sm">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
